import {
  ContentsType,
  NonDeliveryOption,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import { CustomsDeclarationSchema } from "@redotech/redo-fulfillment-customs-common/customs-declaration";
import {
  convertWeight,
  Length,
  LengthUnit,
  LengthUnitSchema,
  Weight,
  WeightSchema,
  WeightUnit,
  WeightUnitSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import * as zExt from "@redotech/rpc/ext";
import { isEnumValue } from "@redotech/util/enum";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type { FulfillmentOrderLineItem } from "../fulfillments/fulfillment-order-line-item";
import { groupFulfillmentOrderLineItems } from "../fulfillments/fulfillment-order-line-item";
import { HazmatSchema } from "../fulfillments/hazmat";
import { OutboundShipmentParcel } from "../fulfillments/outbound-shipment";

export interface DimensionsInInches {
  widthInInches: number;
  heightInInches: number;
  lengthInInches: number;
}

export enum CustomParcelType {
  BOX = "box",
  ENVELOPE = "envelope",
  SOFT_PACK = "soft_pack",
}
export const CustomParcelTypeSchema = createEnum(CustomParcelType, {
  enumName: "CustomParcelType",
});

export const OutboundParcelCustomsSchema = z.object({
  declarations: z.array(CustomsDeclarationSchema).nullish(),
  itn: z.string().nullish(),
  taxIdentifierIds: z.array(z.string()).nullish(),
  uspsExemptionCode: createEnum(USPSExemptionCode, {
    enumName: "USPSExemptionCode",
  }).nullish(),
  additionalCustomsComments: z.string().nullish(),
  contentsExplanation: z.string().nullish(),
  contentsType: createEnum(ContentsType, {
    enumName: "ContentsType",
  }).nullish(),
  overwritten: z.boolean().nullish(),
  nonDeliveryOption: createEnum(NonDeliveryOption, {
    enumName: "NonDeliveryOption",
  }).nullish(),
});
export type OutboundParcelCustoms = z.infer<typeof OutboundParcelCustomsSchema>;
export interface IOutboundParcelCustoms
  extends z.ZodType<OutboundParcelCustoms> {}

export enum ParcelType {
  CUSTOM = "custom",
  CARRIER = "carrier",
}
export const ParcelTypeSchema = createEnum(ParcelType, {
  enumName: "ParcelType",
});

export const BaseParcel = z.object({
  _id: zExt.objectId().optional(),
  name: z.string(),
  hashedName: z.string().nullish(),
  parcelType: ParcelTypeSchema,
  default: z.boolean().nullish(),
  largePackageIndicator: z.boolean().nullish(),
  additionalHandlingIndicator: z.boolean().nullish(),
  shipperReleaseIndicator: z.boolean().nullish(),
});

export const CustomParcelSchema = BaseParcel.extend({
  parcelType: z.literal(ParcelType.CUSTOM),
  type: CustomParcelTypeSchema,
  length: z.number(),
  width: z.number(),
  height: z.number(),
  lengthUnit: LengthUnitSchema,
  weight: z.number(),
  weightUnit: WeightUnitSchema,
  position: z.number().nullish(),
});
export type CustomParcel = z.infer<typeof CustomParcelSchema>;
export function isCustomParcel(parcel: Parcel): parcel is CustomParcel {
  return parcel.parcelType === ParcelType.CUSTOM;
}

export const CarrierParcelSchema = BaseParcel.extend({
  parcelType: z.literal(ParcelType.CARRIER),
  carrier: z.string(),
  description: z.string().nullable(),
  dimensions: z.array(z.string()),
  human_readable: z.string().nullable(),
  max_weight: z.number().nullable(),
  position: z.number().nullish(),
});

export type CarrierParcel = z.infer<typeof CarrierParcelSchema>;

function isCarrierParcel(parcel: Parcel): parcel is CarrierParcel {
  return parcel.parcelType === ParcelType.CARRIER;
}

export const ParcelSchema = z.discriminatedUnion("parcelType", [
  CustomParcelSchema,
  CarrierParcelSchema,
]);

export type Parcel = CarrierParcel | CustomParcel;
export interface IParcel extends z.ZodType<Parcel> {}

/**
 * Document-level schema representing how parcels are stored in MongoDB.
 * The parcel data is nested under the `parcel` field.
 */
export const ParcelDocumentSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  parcel: ParcelSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type ParcelDocument = z.infer<typeof ParcelDocumentSchema>;

const OutboundParcelSchema_ = ParcelSchema.and(
  z.object({
    parcelAndContentWeight: WeightSchema.nullish(),
    insurance: z
      .object({
        // @deprecated - kept for backwards compatibility. New logic only uses insuredAmount.
        // Old behavior: includeInsurance=true meant "insure the full value"
        // New behavior: if insuredAmount is null, package gets max(carrierBaseThreshold, insuranceIncludedThreshold) coverage
        includeInsurance: z.boolean().nullish(),
        // If null, the package gets max(carrierBaseThreshold, insuranceIncludedThreshold) coverage
        // If set to a value, that's the insured amount to use
        insuredAmount: z.number().nullish(),
      })
      .nullish(),
    customs: OutboundParcelCustomsSchema.nullish(),
    dryIceWeight: WeightSchema.nullish(),
    hazmat: HazmatSchema.nullish(),
    itemAllocations: z
      .array(
        z.object({
          externalId: z.string(),
          sku: z.string(),
          quantity: z.number(),
        }),
      )
      .nullish(),
  }),
);
export const OutboundParcelSchema: z.ZodType<OutboundParcel> =
  OutboundParcelSchema_;
export type OutboundParcel = {} & z.infer<typeof OutboundParcelSchema_>;

export function getPackageWeightInOunces(parcel: CustomParcel): number {
  return convertWeight(
    { value: parcel.weight, unit: parcel.weightUnit },
    WeightUnit.OUNCE,
  ).value;
}

export function getPackageDimensionInInches(parcel: CustomParcel): {
  height: number;
  width: number;
  length: number;
} {
  return {
    height:
      parcel?.lengthUnit === LengthUnit.INCH
        ? parcel.height
        : parcel.height / 2.54,
    width:
      parcel?.lengthUnit === LengthUnit.INCH
        ? parcel.width
        : parcel.width / 2.54,
    length:
      parcel?.lengthUnit === LengthUnit.INCH
        ? parcel.length
        : parcel.length / 2.54,
  };
}

export function standardizePackageDimensions(parcel: Parcel): Parcel {
  if (isCarrierParcel(parcel)) {
    return parcel;
  } else {
    const { height, width, length } = getPackageDimensionInInches(parcel);
    return {
      ...parcel,
      weight: getPackageWeightInOunces(parcel),
      height,
      width,
      length,
      lengthUnit: LengthUnit.INCH,
      weightUnit: WeightUnit.OUNCE,
    };
  }
}

// https://www.easypost.com/service-levels-and-parcels
export enum OneRateParcelToPreDefinedPackage {
  FEDEX_ENVELOPE = "FedExEnvelope",
  FEDEX_EXTRA_SMALL_BOX = "FedExExtraSmallBox",
  FEDEX_SMALL_BOX = "FedExSmallBox",
  FEDEX_MEDIUM_BOX = "FedExMediumBox",
  FEDEX_LARGE_BOX = "FedExLargeBox",
  FEDEX_PAK = "FedExPak",
  FEDEX_EXTRA_LARGE_BOX = "FedExExtraLargeBox",
}

// https://developer.fedex.com/api/en-us/catalog/rate/v1/docs.html
export enum FedexOneRateParcel {
  FEDEX_ENVELOPE = "FEDEX_ENVELOPE",
  FEDEX_EXTRA_SMALL_BOX = "FEDEX_EXTRA_SMALL_BOX",
  FEDEX_SMALL_BOX = "FEDEX_SMALL_BOX",
  FEDEX_MEDIUM_BOX = "FEDEX_MEDIUM_BOX",
  FEDEX_LARGE_BOX = "FEDEX_LARGE_BOX",
  FEDEX_PAK = "FEDEX_PAK",
  FEDEX_EXTRA_LARGE_BOX = "FEDEX_EXTRA_LARGE_BOX",
}

export function fedexOneRateParcelDisplayName(
  parcel: FedexOneRateParcel,
): string {
  switch (parcel) {
    case FedexOneRateParcel.FEDEX_ENVELOPE:
      return "FedEx Envelope";
    case FedexOneRateParcel.FEDEX_PAK:
      return "FedEx Pak";
    case FedexOneRateParcel.FEDEX_EXTRA_SMALL_BOX:
      return "FedEx Extra Small Box";
    case FedexOneRateParcel.FEDEX_SMALL_BOX:
      return "FedEx Small Box";
    case FedexOneRateParcel.FEDEX_MEDIUM_BOX:
      return "FedEx Medium Box";
    case FedexOneRateParcel.FEDEX_LARGE_BOX:
      return "FedEx Large Box";
    case FedexOneRateParcel.FEDEX_EXTRA_LARGE_BOX:
      return "FedEx Extra Large Box";
    default:
      assertNever(parcel);
  }
}

export function predefinedPackageToOneRateParcel(
  predefinedPackage: string | null | undefined,
): FedexOneRateParcel | null {
  if (!predefinedPackage) {
    return null;
  }
  switch (predefinedPackage) {
    case OneRateParcelToPreDefinedPackage.FEDEX_PAK:
      return FedexOneRateParcel.FEDEX_PAK;
    case OneRateParcelToPreDefinedPackage.FEDEX_EXTRA_SMALL_BOX:
      return FedexOneRateParcel.FEDEX_EXTRA_SMALL_BOX;
    case OneRateParcelToPreDefinedPackage.FEDEX_SMALL_BOX:
      return FedexOneRateParcel.FEDEX_SMALL_BOX;
    case OneRateParcelToPreDefinedPackage.FEDEX_MEDIUM_BOX:
      return FedexOneRateParcel.FEDEX_MEDIUM_BOX;
    case OneRateParcelToPreDefinedPackage.FEDEX_LARGE_BOX:
      return FedexOneRateParcel.FEDEX_LARGE_BOX;
    case OneRateParcelToPreDefinedPackage.FEDEX_EXTRA_LARGE_BOX:
      return FedexOneRateParcel.FEDEX_EXTRA_LARGE_BOX;
    case OneRateParcelToPreDefinedPackage.FEDEX_ENVELOPE:
      return FedexOneRateParcel.FEDEX_ENVELOPE;
    default:
      return null;
  }
}

export function oneRateParcelToPredefinedPackage(
  fedexOneRateParcel: FedexOneRateParcel,
): OneRateParcelToPreDefinedPackage | null {
  switch (fedexOneRateParcel) {
    case FedexOneRateParcel.FEDEX_ENVELOPE:
      return OneRateParcelToPreDefinedPackage.FEDEX_ENVELOPE;
    case FedexOneRateParcel.FEDEX_EXTRA_SMALL_BOX:
      return OneRateParcelToPreDefinedPackage.FEDEX_EXTRA_SMALL_BOX;
    case FedexOneRateParcel.FEDEX_SMALL_BOX:
      return OneRateParcelToPreDefinedPackage.FEDEX_SMALL_BOX;
    case FedexOneRateParcel.FEDEX_MEDIUM_BOX:
      return OneRateParcelToPreDefinedPackage.FEDEX_MEDIUM_BOX;
    case FedexOneRateParcel.FEDEX_LARGE_BOX:
      return OneRateParcelToPreDefinedPackage.FEDEX_LARGE_BOX;
    case FedexOneRateParcel.FEDEX_EXTRA_LARGE_BOX:
      return OneRateParcelToPreDefinedPackage.FEDEX_EXTRA_LARGE_BOX;
    case FedexOneRateParcel.FEDEX_PAK:
      return OneRateParcelToPreDefinedPackage.FEDEX_PAK;
    default:
      return null;
  }
}

export interface ParcelDimension {
  length: number | null | undefined;
  width: number | null | undefined;
  height: number | null | undefined;
  lengthUnit: LengthUnit;
  parcelType: CustomParcelType | undefined;
}

const oneRateVolumeSpecs = [
  {
    parcel: FedexOneRateParcel.FEDEX_ENVELOPE,
    spec: { maxCubicInches: 300, parcelType: CustomParcelType.ENVELOPE },
  },
  {
    parcel: FedexOneRateParcel.FEDEX_EXTRA_SMALL_BOX,
    spec: { maxCubicInches: 168, parcelType: CustomParcelType.BOX },
  },
  {
    parcel: FedexOneRateParcel.FEDEX_SMALL_BOX,
    spec: { maxCubicInches: 420, parcelType: CustomParcelType.BOX },
  },
  {
    parcel: FedexOneRateParcel.FEDEX_PAK,
    spec: { maxCubicInches: 650, parcelType: CustomParcelType.SOFT_PACK },
  },
  {
    parcel: FedexOneRateParcel.FEDEX_MEDIUM_BOX,
    spec: { maxCubicInches: 650, parcelType: CustomParcelType.BOX },
  },
  {
    parcel: FedexOneRateParcel.FEDEX_LARGE_BOX,
    spec: { maxCubicInches: 1100, parcelType: CustomParcelType.BOX },
  },
  {
    parcel: FedexOneRateParcel.FEDEX_EXTRA_LARGE_BOX,
    spec: { maxCubicInches: 2200, parcelType: CustomParcelType.BOX },
  },
].sort((a, b) => a.spec.maxCubicInches - b.spec.maxCubicInches);

//https://docs.easypost.com/carriers/fedex-guide#predefined-packages
const oneRateFedexParcelDimensions: Record<
  FedexOneRateParcel,
  ParcelDimension[]
> = {
  FEDEX_ENVELOPE: [
    {
      length: 9.5,
      width: 12.5,
      height: 0,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.ENVELOPE,
    },
  ],
  FEDEX_PAK: [
    {
      length: 12.5,
      width: 15.5,
      height: 0,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.SOFT_PACK,
    },
  ],
  FEDEX_EXTRA_SMALL_BOX: [
    {
      length: 7.25,
      width: 2.5,
      height: 4.7,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
  ],
  FEDEX_SMALL_BOX: [
    {
      length: 12.25,
      width: 10.9,
      height: 1.5,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
    {
      length: 8.75,
      width: 2.63,
      height: 11.25,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
  ],
  FEDEX_MEDIUM_BOX: [
    {
      length: 13.25,
      width: 11.5,
      height: 2.38,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
    {
      length: 8.75,
      width: 4.38,
      height: 11.25,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
  ],
  FEDEX_LARGE_BOX: [
    {
      length: 17.88,
      width: 12.38,
      height: 3,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
    {
      length: 8.75,
      width: 7.75,
      height: 11.25,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
  ],
  FEDEX_EXTRA_LARGE_BOX: [
    {
      length: 11.88,
      width: 10.75,
      height: 11,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
    {
      length: 15.25,
      width: 14.13,
      height: 6,
      lengthUnit: LengthUnit.INCH,
      parcelType: CustomParcelType.BOX,
    },
  ],
};

// Given custom dimensions and a one rate dimension, check if the custom dimensions are within tolerance of the one rate dimensions
export function dimensionsWithinTolerance(
  customDimensions: ParcelDimension,
  oneRatePackageDimensions: ParcelDimension,
): boolean {
  const customDimsInches = {
    width: dimensionInInches(
      customDimensions.width,
      customDimensions.lengthUnit,
    ),
    height: dimensionInInches(
      customDimensions.height,
      customDimensions.lengthUnit,
    ),
    length: dimensionInInches(
      customDimensions.length,
      customDimensions.lengthUnit,
    ),
  };

  const oneRatePackageDimsInches = {
    width: dimensionInInches(
      oneRatePackageDimensions.width,
      oneRatePackageDimensions.lengthUnit,
    ),
    height: dimensionInInches(
      oneRatePackageDimensions.height,
      oneRatePackageDimensions.lengthUnit,
    ),
    length: dimensionInInches(
      oneRatePackageDimensions.length,
      oneRatePackageDimensions.lengthUnit,
    ),
  };

  const [a, b, c] = Object.values(customDimsInches);
  const [x, y, z] = Object.values(oneRatePackageDimsInches);

  // Check all permutations of the dimensions to see if any of them are within tolerance
  // Ex: 10x5x1 should be within tolerance of 1x10x5 - the length/width/height can be in any order
  // 10x5x1 should NOT match 10x10x1 because one of the dimensions is not within tolerance
  return (
    (withinTolerance(a, x) && withinTolerance(b, y) && withinTolerance(c, z)) ||
    (withinTolerance(a, x) && withinTolerance(b, z) && withinTolerance(c, y)) ||
    (withinTolerance(a, y) && withinTolerance(b, x) && withinTolerance(c, z)) ||
    (withinTolerance(a, y) && withinTolerance(b, z) && withinTolerance(c, x)) ||
    (withinTolerance(a, z) && withinTolerance(b, x) && withinTolerance(c, y)) ||
    (withinTolerance(a, z) && withinTolerance(b, y) && withinTolerance(c, x))
  );
}

function dimensionInInches(
  dimension: number | null | undefined,
  unit: LengthUnit,
): number {
  if (!dimension) {
    return 1;
  }
  return unit === LengthUnit.INCH ? dimension : dimension / 2.54;
}

// Dimension1 must be no larger than 20% larger than dimension2
function withinTolerance(
  dimension1: number | null | undefined,
  dimension2: number | null | undefined,
): boolean {
  if (!dimension1 || !dimension2) {
    return false;
  }

  // 20% tolerance
  const tolerance = 0.2;
  const maxAllowed = dimension2 * (1 + tolerance);
  return dimension1 <= maxAllowed;
}

export interface OneRateMatch {
  parcel: FedexOneRateParcel;
  dimensionWarning: boolean;
}

// Finds the smallest FedexOneRateParcel whose cubic inch limit fits the custom parcel.
// Returns dimensionWarning: true if the volume fits but dimensions don't match any physical box.
export function getMatchingOneRateParcel(
  parcel: ParcelDimension,
): OneRateMatch | null {
  const volumeCubicInches = getVolume(parcel);

  for (const { parcel: oneRateParcel, spec } of oneRateVolumeSpecs) {
    if (spec.parcelType !== parcel.parcelType) {
      continue;
    }
    if (volumeCubicInches > spec.maxCubicInches) {
      continue;
    }
    const dimensionsFit = oneRateFedexParcelDimensions[oneRateParcel].some(
      (dimensionOption) => dimensionsWithinTolerance(parcel, dimensionOption),
    );
    return { parcel: oneRateParcel, dimensionWarning: !dimensionsFit };
  }
  return null;
}

function getVolume(parcel: ParcelDimension): number {
  const widthInInches = dimensionInInches(parcel.width, parcel.lengthUnit);
  const heightInInches = dimensionInInches(parcel.height, parcel.lengthUnit);
  const lengthInInches = dimensionInInches(parcel.length, parcel.lengthUnit);
  return widthInInches * heightInInches * lengthInInches;
}

export function getParcelWeightGrams(parcel: Parcel | null): Weight {
  // If we want to do this for carrier parcels, we will have to go figure out the weight of each type of parcel
  // Easypost does not provide that information via the API
  if (!parcel || parcel.parcelType === ParcelType.CARRIER) {
    return { value: 0, unit: WeightUnit.GRAM };
  }
  return convertWeight(
    { value: parcel.weight, unit: parcel.weightUnit },
    WeightUnit.GRAM,
  );
}

export interface ItemAllocation {
  externalId: string;
  sku: string;
  quantity: number;
}

/**
 * Remove allocations for items not in lineItems, cap over-allocated quantities,
 * and strip zero-quantity entries. When total allocations for an item exceed
 * the item's quantity, trims excess from later parcels (earlier parcels keep
 * their share first).
 */
export function cleanAllocations(
  parcels: OutboundParcel[],
  lineItems: readonly FulfillmentOrderLineItem[],
): OutboundParcel[] {
  const validIds = new Set(lineItems.map((item) => item.externalId));
  const itemQuantities = new Map(
    lineItems.map((item) => [item.externalId, item.quantity]),
  );

  // First: sum total allocations per item across all parcels
  const totalAllocated = new Map<string, number>();
  for (const parcel of parcels) {
    for (const a of parcel.itemAllocations ?? []) {
      if (validIds.has(a.externalId) && a.quantity > 0) {
        totalAllocated.set(
          a.externalId,
          (totalAllocated.get(a.externalId) ?? 0) + a.quantity,
        );
      }
    }
  }

  // Only cap items that are actually over-allocated
  const remaining = new Map<string, number>();
  for (const [externalId, qty] of itemQuantities) {
    const allocated = totalAllocated.get(externalId) ?? 0;
    if (allocated > qty) {
      // Over-allocated: trim from later parcels
      remaining.set(externalId, qty);
    }
  }

  return parcels.map((parcel) => ({
    ...parcel,
    itemAllocations: (parcel.itemAllocations ?? [])
      .filter((a) => validIds.has(a.externalId) && a.quantity > 0)
      .map((a) => {
        if (!remaining.has(a.externalId)) {
          // Not over-allocated, keep as-is
          return a;
        }
        const available = remaining.get(a.externalId)!;
        const capped = Math.min(a.quantity, available);
        remaining.set(a.externalId, available - capped);
        return { ...a, quantity: capped };
      })
      .filter((a) => a.quantity > 0),
  }));
}

/**
 * Ensure every line item is allocated to some parcel. Unallocated items
 * (or unallocated remainder) get pushed onto parcel 0.
 */
export function assignUnallocatedItems(
  parcels: OutboundParcel[],
  lineItems: readonly FulfillmentOrderLineItem[],
): OutboundParcel[] {
  const allocatedQuantities = new Map<string, number>();
  for (const parcel of parcels) {
    for (const alloc of parcel.itemAllocations ?? []) {
      allocatedQuantities.set(
        alloc.externalId,
        (allocatedQuantities.get(alloc.externalId) ?? 0) + alloc.quantity,
      );
    }
  }

  const extras: ItemAllocation[] = [];
  for (const item of lineItems) {
    const remaining =
      item.quantity - (allocatedQuantities.get(item.externalId) ?? 0);
    if (remaining > 0) {
      extras.push({
        externalId: item.externalId,
        sku: item.sku ?? "",
        quantity: remaining,
      });
    }
  }

  if (extras.length === 0) {
    return parcels;
  }

  const firstAllocations = [...(parcels[0]!.itemAllocations ?? [])];
  for (const extra of extras) {
    const existing = firstAllocations.find(
      (a) => a.externalId === extra.externalId,
    );
    if (existing) {
      const index = firstAllocations.indexOf(existing);
      firstAllocations[index] = {
        ...existing,
        quantity: existing.quantity + extra.quantity,
      };
    } else {
      firstAllocations.push(extra);
    }
  }

  const result = [...parcels];
  result[0] = { ...result[0]!, itemAllocations: firstAllocations };
  return result;
}

/**
 * Compute parcelAndContentWeight for each parcel from its allocated items.
 * Parcels with an existing parcelAndContentWeight (user override) are unchanged.
 */
export function computeParcelWeights(
  parcels: OutboundParcel[],
  lineItems: readonly FulfillmentOrderLineItem[],
): OutboundParcel[] {
  const itemWeightGrams = new Map<string, number>();
  for (const item of lineItems) {
    const w = item.inventoryItemWeight ?? item.weight;
    itemWeightGrams.set(
      item.externalId,
      convertWeight(w, WeightUnit.GRAM).value,
    );
  }

  return parcels.map((parcel) => {
    if (parcel.parcelAndContentWeight) {
      return parcel;
    }
    const parcelWeightGrams = getParcelWeightGrams(parcel).value;
    let contentsWeightGrams = 0;
    for (const alloc of parcel.itemAllocations ?? []) {
      contentsWeightGrams +=
        (itemWeightGrams.get(alloc.externalId) ?? 0) * alloc.quantity;
    }
    return {
      ...parcel,
      parcelAndContentWeight: {
        value: contentsWeightGrams + parcelWeightGrams,
        unit: WeightUnit.GRAM as const,
      },
    };
  });
}

/**
 * Clean up allocations and compute parcel weights.
 * - Strips stale allocations (items not in lineItems).
 * - Caps over-allocated quantities to match line item quantities.
 * - Pushes unallocated items onto the first parcel's itemAllocations.
 * - If parcelAndContentWeight is set, it's a user override — keep it.
 * - Otherwise, compute weight from allocated line items + parcel weight.
 */
export function applyParcelWeightDistribution(
  parcels: OutboundParcel[] | null | undefined,
  lineItems: readonly FulfillmentOrderLineItem[],
): { parcels: OutboundParcel[]; displayWeightGrams: number } {
  const dedupedLineItems = groupFulfillmentOrderLineItems(lineItems);

  if (!parcels?.length) {
    const totalWeightGrams = dedupedLineItems.reduce(
      (acc, item) =>
        acc +
        convertWeight(item.inventoryItemWeight ?? item.weight, WeightUnit.GRAM)
          .value *
          item.quantity,
      0,
    );
    return { parcels: [], displayWeightGrams: totalWeightGrams };
  }

  const cleaned = cleanAllocations(parcels, dedupedLineItems);
  const allocated = assignUnallocatedItems(cleaned, dedupedLineItems);
  const result = computeParcelWeights(allocated, dedupedLineItems);

  return {
    parcels: result,
    displayWeightGrams: result.reduce(
      (acc, p) =>
        acc + convertWeight(p.parcelAndContentWeight!, WeightUnit.GRAM).value,
      0,
    ),
  };
}

export function areParcelsEqual(a: OutboundParcel[], b: OutboundParcel[]) {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }
  for (const [i, element] of a.entries()) {
    if (!!b[i] && !isParcelEqual(element, b[i])) {
      return false;
    }
  }
  return true;
}

export function isParcelEqual(a: OutboundParcel, b: OutboundParcel) {
  if (
    Boolean(a.largePackageIndicator) !== Boolean(b.largePackageIndicator) ||
    Boolean(a.additionalHandlingIndicator) !==
      Boolean(b.additionalHandlingIndicator) ||
    Boolean(a.shipperReleaseIndicator) !== Boolean(b.shipperReleaseIndicator)
  ) {
    return false;
  }

  if (
    a.parcelType === ParcelType.CARRIER &&
    b.parcelType === ParcelType.CARRIER
  ) {
    return a.carrier === b.carrier && a.name === b.name;
  } else if (
    a.parcelType === ParcelType.CUSTOM &&
    b.parcelType === ParcelType.CUSTOM
  ) {
    return (
      a.weight === b.weight &&
      a.width === b.width &&
      a.height === b.height &&
      a.length === b.length &&
      a.lengthUnit === b.lengthUnit &&
      a.weightUnit === b.weightUnit
    );
  } else {
    return false;
  }
}

/** True when a weight is expressed in a metric unit (g/kg/t) rather than imperial. */
export function isMetricWeightUnit(unit: WeightUnit): boolean {
  return unit !== WeightUnit.OUNCE && unit !== WeightUnit.POUND;
}

export function isMetricWeight(weight: Weight | null | undefined): boolean {
  return weight != null && isMetricWeightUnit(weight.unit);
}

/**
 * Normalize a parcel's combined weight for a V2 rate request, matching V1's
 * behavior so the two engines quote the same carriers. Metric-vs-imperial is
 * decided by the merchant's configured `parcelWeightUnit`, NOT by the weight's
 * own unit: `applyParcelWeightDistribution` emits the combined weight in GRAMS
 * for every shipment, so keying off `weight.unit` would mislabel every merchant
 * metric and skip the ounce path — leaving imperial merchants a whole pound off
 * V1 once UPS rounds billing weight up (a float-dirty 15.0000000001 lb → 16).
 * - Imperial merchants (oz/lb unit) launder in OUNCES, exactly as V1 does (V1
 *   carries the weight as ounces via `convertWeight(w, OUNCE)` and only then
 *   converts to lb at the carrier). `roundIfVeryClose`'s 0.001 tolerance in
 *   ounces snaps a tighter band than in pounds, keeping the engines bit-identical.
 * - Metric merchants (g/kg/t unit) launder in their own unit: a metric merchant
 *   must NOT be switched to imperial for the shipping provider.
 * - Floor a missing or zero weight to 1 oz (or its metric equivalent) so the
 *   carrier doesn't reject the request outright (which drops the rate entirely).
 */
export function normalizeParcelWeightV2(
  weight: Weight | null | undefined,
  parcelWeightUnit: WeightUnit,
): Weight {
  const merchantIsMetric = isMetricWeightUnit(parcelWeightUnit);
  // Default a missing weight to 1 oz so it shares the exact same floor as a
  // zero weight (the metric branch converts it to the 1-oz metric equivalent).
  // Defaulting to `{1, parcelWeightUnit}` would floor a metric parcel to a whole
  // kilogram (~35 oz) and inflate rates.
  const raw = weight ?? { value: 1, unit: WeightUnit.OUNCE };
  if (merchantIsMetric) {
    const laundered = convertWeight(raw, parcelWeightUnit);
    return laundered.value > 0
      ? laundered
      : convertWeight({ value: 1, unit: WeightUnit.OUNCE }, parcelWeightUnit);
  }
  const ounces = convertWeight(raw, WeightUnit.OUNCE);
  return ounces.value > 0 ? ounces : { value: 1, unit: WeightUnit.OUNCE };
}

/**
 * Normalize one parcel dimension for a V2 rate request, matching V1's
 * getParcelInfoWithDefaults (`dimension > 0 ? dimension : 1`): a missing or
 * zero/negative dimension is floored to 1 inch so carriers don't reject the
 * request and drop the rate. EasyPost in particular rejects a Shipment.create
 * whose parcel mixes non-zero dimensions with a zero one ("Wrong parameter
 * type."), dropping every rate for the shipment.
 *
 * A positive dimension keeps the merchant's selected unit — only the degenerate
 * zero/empty case is coerced to 1 inch, exactly as [[normalizeParcelWeightV2]]
 * preserves the weight unit and only substitutes the degenerate zero case.
 */
export function normalizeParcelDimensionV2(
  length: Length | null | undefined,
): Length {
  return length && length.value > 0
    ? length
    : { value: 1, unit: LengthUnit.INCH };
}

export function getParcelInfoWithDefaults(
  standardizedParcel: OutboundParcel,
  weightInOunces: number,
) {
  return isCarrierParcel(standardizedParcel)
    ? {
        predefined_package: standardizedParcel.name,
        weight: weightInOunces > 0 ? weightInOunces : 1,
        largePackageIndicator:
          standardizedParcel.largePackageIndicator ?? false,
        additionalHandlingIndicator:
          standardizedParcel.additionalHandlingIndicator ?? false,
        shipperReleaseIndicator:
          standardizedParcel.shipperReleaseIndicator ?? false,
      }
    : {
        weight: weightInOunces > 0 ? weightInOunces : 1,
        length: standardizedParcel.length > 0 ? standardizedParcel.length : 1,
        width: standardizedParcel.width > 0 ? standardizedParcel.width : 1,
        height: standardizedParcel.height > 0 ? standardizedParcel.height : 1,
        parcelType: standardizedParcel.type,
        largePackageIndicator:
          standardizedParcel.largePackageIndicator ?? false,
        additionalHandlingIndicator:
          standardizedParcel.additionalHandlingIndicator ?? false,
        shipperReleaseIndicator:
          standardizedParcel.shipperReleaseIndicator ?? false,
      };
}

export const INCHES_PER_CUBIC_FOOT = 1728;

// We know that USPS rounds package dimensions to the nearest inch
export function getCubicFeet(
  length: number | null | undefined,
  width: number | null | undefined,
  height: number | null | undefined,
): number {
  return ((length ?? 1) * (width ?? 1) * (height ?? 1)) / INCHES_PER_CUBIC_FOOT;
}

// We think Fedex rounds package dimensions UP
export function getCubicInchesRoundedUp(
  length: number | null | undefined,
  width: number | null | undefined,
  height: number | null | undefined,
): number {
  const roundedLength = Math.ceil(length ?? 1);
  const roundedWidth = Math.ceil(width ?? 1);
  const roundedHeight = Math.ceil(height ?? 1);
  return roundedLength * roundedWidth * roundedHeight;
}

// https://docs.google.com/spreadsheets/d/1_EgLibifJa00C8GOKRlB6xnIHYJYEdITVOx2BWq59nk/edit?gid=0#gid=0
export enum USPSFlatRatePriorityParcel {
  FLAT_RATE_ENVELOPE = "FlatRateEnvelope",
  FLAT_RATE_WINDOW_ENVELOPE = "FlatRateWindowEnvelope",
  FLAT_RATE_CARDBOARD_ENVELOPE = "FlatRateCardboardEnvelope",
  SMALL_FLAT_RATE_ENVELOPE = "SmallFlatRateEnvelope",
  FLAT_RATE_LEGAL_ENVELOPE = "FlatRateLegalEnvelope",
  FLAT_RATE_PADDED_ENVELOPE = "FlatRatePaddedEnvelope",
  SMALL_FLAT_RATE_BOX = "SmallFlatRateBox",
  MEDIUM_FLAT_RATE_BOX = "MediumFlatRateBox",
  LARGE_FLAT_RATE_BOX = "LargeFlatRateBox",
}

export enum USPSFlatRateExpressParcel {
  FLAT_RATE_ENVELOPE = "FlatRateEnvelope",
  FLAT_RATE_LEGAL_ENVELOPE = "FlatRateLegalEnvelope",
  FLAT_RATE_PADDED_ENVELOPE = "FlatRatePaddedEnvelope",
}

export const DEFAULT_REDO_PARCEL: CustomParcel = {
  name: "Redo Default",
  parcelType: ParcelType.CUSTOM,
  default: true,
  type: CustomParcelType.BOX,
  length: 10,
  width: 10,
  height: 10,
  lengthUnit: LengthUnit.INCH,
  weight: 10,
  weightUnit: WeightUnit.OUNCE,
};

// https://www.easypost.com/service-levels-and-parcels?carrier=UPS
export enum EasypostUpsPreDefinedParcel {
  UPSLetter = "UPSLetter",
  UPSExpressBox = "UPSExpressBox",
  UPS25kgBox = "UPS25kgBox",
  UPS10kgBox = "UPS10kgBox",
  Tube = "Tube",
  Pak = "Pak",
  SmallExpressBox = "SmallExpressBox",
  MediumExpressBox = "MediumExpressBox",
  LargeExpressBox = "LargeExpressBox",
}

export enum UpsPackageType {
  // UPS has told us that "Unknown" is invalid, even though it's in their docs. They recommend using "Package" instead
  // Unknown = "00",
  UPSLetter = "01",
  CustomerSuppliedPackage = "02",
  Tube = "03",
  Pak = "04",
  ExpressBox = "21",
  TwentyFiveKgBox = "24",
  TenKgBox = "25",
  Pallet = "30",
  SmallExpressBox = "2a",
  MediumExpressBox = "2b",
  LargeExpressBox = "2c",
}

export const easypostPreDefinedParcelToUpsPackageType: Record<
  EasypostUpsPreDefinedParcel,
  UpsPackageType
> = {
  [EasypostUpsPreDefinedParcel.UPSLetter]: UpsPackageType.UPSLetter,
  [EasypostUpsPreDefinedParcel.UPSExpressBox]:
    UpsPackageType.CustomerSuppliedPackage,
  [EasypostUpsPreDefinedParcel.UPS25kgBox]: UpsPackageType.TwentyFiveKgBox,
  [EasypostUpsPreDefinedParcel.UPS10kgBox]: UpsPackageType.TenKgBox,
  [EasypostUpsPreDefinedParcel.Tube]: UpsPackageType.Tube,
  [EasypostUpsPreDefinedParcel.Pak]: UpsPackageType.Pak,
  [EasypostUpsPreDefinedParcel.SmallExpressBox]: UpsPackageType.SmallExpressBox,
  [EasypostUpsPreDefinedParcel.MediumExpressBox]:
    UpsPackageType.MediumExpressBox,
  [EasypostUpsPreDefinedParcel.LargeExpressBox]: UpsPackageType.LargeExpressBox,
};

export function isUpsPredefinedParcel(
  preDefinedPackage: string | undefined | null,
): preDefinedPackage is EasypostUpsPreDefinedParcel {
  return isEnumValue(preDefinedPackage, EasypostUpsPreDefinedParcel);
}

export function parcelToUpsPackageType(
  parcel: OutboundShipmentParcel,
): UpsPackageType {
  if (isUpsPredefinedParcel(parcel.predefined_package)) {
    return easypostPreDefinedParcelToUpsPackageType[parcel.predefined_package];
  }

  // If the user has not selected a pre defined UPS package, we should tell UPS that the package type is a customer supplied package
  return UpsPackageType.CustomerSuppliedPackage;
}

export function parcelToUpsDimensions(
  parcel: OutboundShipmentParcel,
): ParcelDimension {
  if (isUpsPredefinedParcel(parcel.predefined_package)) {
    // UPS requires every package dimension to be greater than 0, but some
    // predefined packaging (e.g. Pak and UPSLetter) has a 0 height. Floor each
    // dimension to at least 1, matching the custom-parcel branch below.
    const predefined = upsPredefinedParcelDimensions[parcel.predefined_package];
    return {
      ...predefined,
      length: predefined.length || 1,
      width: predefined.width || 1,
      height: predefined.height || 1,
    };
  }

  // Assume we are shipping a "Package" if the parcel type is not specified
  return {
    parcelType: parcel.parcelType ?? CustomParcelType.BOX,
    length: parcel.length ? parseFloat(parcel.length.toFixed(2)) : 1,
    width: parcel.width ? parseFloat(parcel.width.toFixed(2)) : 1,
    height: parcel.height ? parseFloat(parcel.height.toFixed(2)) : 1,
    lengthUnit: LengthUnit.INCH,
  };
}

// https://docs.easypost.com/carriers/ups-guide
const upsPredefinedParcelDimensions: Record<
  EasypostUpsPreDefinedParcel,
  ParcelDimension
> = {
  [EasypostUpsPreDefinedParcel.UPSLetter]: {
    length: 15,
    width: 9.5,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUpsPreDefinedParcel.UPSExpressBox]: {
    length: 12.5,
    width: 3.75,
    height: 18,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.SOFT_PACK,
  },
  [EasypostUpsPreDefinedParcel.UPS25kgBox]: {
    length: 19.75,
    width: 17.75,
    height: 13.25,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUpsPreDefinedParcel.UPS10kgBox]: {
    length: 16.5,
    width: 13.25,
    height: 10.75,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUpsPreDefinedParcel.Tube]: {
    length: 38,
    width: 6,
    height: 6,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUpsPreDefinedParcel.Pak]: {
    length: 16,
    width: 12.75,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.SOFT_PACK,
  },
  [EasypostUpsPreDefinedParcel.SmallExpressBox]: {
    length: 13,
    width: 11,
    height: 2,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUpsPreDefinedParcel.MediumExpressBox]: {
    length: 16,
    width: 11,
    height: 3,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUpsPreDefinedParcel.LargeExpressBox]: {
    length: 18,
    width: 13,
    height: 3,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
};

export enum EasypostUspsPreDefinedParcel {
  Card = "Card",
  Letter = "Letter",
  Flat = "Flat",
  FlatRateEnvelope = "FlatRateEnvelope",
  FlatRateLegalEnvelope = "FlatRateLegalEnvelope",
  FlatRatePaddedEnvelope = "FlatRatePaddedEnvelope",
  FlatRateWindowEnvelope = "FlatRateWindowEnvelope",
  FlatRateCardboardEnvelope = "FlatRateCardboardEnvelope",
  SmallFlatRateEnvelope = "SmallFlatRateEnvelope",
  Parcel = "Parcel",
  SoftPack = "SoftPack",
  SmallFlatRateBox = "SmallFlatRateBox",
  MediumFlatRateBox = "MediumFlatRateBox",
  LargeFlatRateBox = "LargeFlatRateBox",
  LargeFlatRateBoxAPOFPO = "LargeFlatRateBoxAPOFPO",
  FlatTubTrayBox = "FlatTubTrayBox",
  EMMTrayBox = "EMMTrayBox",
  FullTrayBox = "FullTrayBox",
  HalfTrayBox = "HalfTrayBox",
  PMODSack = "PMODSack",
}

// Not all of the predefined parcels are in here - some, like Parcel, are missing dimensions
export const uspsPredefinedParcelDimensions: Record<string, ParcelDimension> = {
  [EasypostUspsPreDefinedParcel.Card]: {
    length: 6,
    width: 4.25, // Easypost docs say 4.5, but its actually 4.25 according to USPS
    height: 0.016,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.Letter]: {
    length: 11.5,
    width: 6.125,
    height: 0.25,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.Flat]: {
    length: 11.5,
    width: 6.125,
    height: 0.75,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.FlatRateEnvelope]: {
    length: 12.5,
    width: 9.5,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.FlatRateLegalEnvelope]: {
    length: 15,
    width: 9.5,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.FlatRatePaddedEnvelope]: {
    length: 12.5,
    width: 9.5,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.FlatRateWindowEnvelope]: {
    length: 10,
    width: 5,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.FlatRateCardboardEnvelope]: {
    length: 12.5,
    width: 9.5,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.SmallFlatRateEnvelope]: {
    length: 10,
    width: 6,
    height: 0,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.ENVELOPE,
  },
  [EasypostUspsPreDefinedParcel.SmallFlatRateBox]: {
    length: 8.6875,
    width: 5.4375,
    height: 1.75,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUspsPreDefinedParcel.MediumFlatRateBox]: {
    length: 11.25,
    width: 8.75,
    height: 6,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUspsPreDefinedParcel.LargeFlatRateBox]: {
    length: 12.25,
    width: 12.25,
    height: 6,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
  [EasypostUspsPreDefinedParcel.LargeFlatRateBoxAPOFPO]: {
    length: 12.25,
    width: 12.25,
    height: 6,
    lengthUnit: LengthUnit.INCH,
    parcelType: CustomParcelType.BOX,
  },
};

export function preserveParcelMetadata(
  newParcel: OutboundParcel,
  existingParcel: OutboundParcel | null | undefined,
): OutboundParcel {
  if (!existingParcel) {
    return newParcel;
  }
  return {
    ...newParcel,
    insurance: newParcel.insurance ?? existingParcel.insurance,
    customs: newParcel.customs ?? existingParcel.customs,
    hazmat: newParcel.hazmat ?? existingParcel.hazmat,
    dryIceWeight: newParcel.dryIceWeight ?? existingParcel.dryIceWeight,
  };
}

export function removeParcelMetadata(
  parcels: OutboundParcel[] | null | undefined,
): OutboundParcel[] | undefined {
  if (!parcels) {
    return undefined;
  }

  return parcels.map((parcel) => ({
    ...parcel,
    insurance: undefined,
    customs: undefined,
    dryIceWeight: undefined,
    hazmat: undefined,
  }));
}

function isUspsPreDefinedParcel(parcelName: string): boolean {
  return isEnumValue(parcelName, EasypostUspsPreDefinedParcel);
}

function isFedexOneRateParcel(parcelName: string): boolean {
  return isEnumValue(parcelName, OneRateParcelToPreDefinedPackage);
}

export function isPreDefinedParcel(name: string): boolean {
  return (
    isUspsPreDefinedParcel(name) ||
    isUpsPredefinedParcel(name) ||
    isFedexOneRateParcel(name)
  );
}
